import { ReactElement } from 'react';

import {
   ConfidentialResourceAccessRequestCreateCommand,
   ConfidentialResourceAccessSelfGrantCommand,
   ConfidentialResourceType,
} from '@dreryk/egabinet-medical-data-confidentiality-frontend-api';
import { Query } from '@dreryk/command-query-api';

import { refetchCqrsQuery, useCqrsCommand } from '@dreryk/command-query-react-common';
import { PromiseVoidFunction, Translate } from '@dreryk/react-common';
import {
   Button,
   ButtonVariant,
   dispatchSnackbarPositiveEvent,
   IconFillColor,
   IconSize,
   IconWrapper,
   ShieldProtectedIcon,
   StyledFlex,
} from '@dreryk/react-components';

import { useCurrentPractitionerGlobalState } from '~libs.shared.account';

import { translationKeys } from './translations';

type ConfidentialAccessRequestButtonProps = {
   readonly resourceId: number;
   readonly resourceType: ConfidentialResourceType;
   readonly refetchQuery: new () => Query<unknown>;
};

export const ConfidentialAccessRequestButton = (props: ConfidentialAccessRequestButtonProps): ReactElement | null => {
   const { getCurrentPractitioner } = useCurrentPractitionerGlobalState();
   const confidentialAccessLevel = getCurrentPractitioner()?.confidentialAccessLevel;

   const { executeAsync: requestAccess } = useCqrsCommand<void, void>(
      () => {
         const currentConfidentialAccessLevel = getCurrentPractitioner()?.confidentialAccessLevel;
         const Command =
            currentConfidentialAccessLevel === 'MONITORED'
               ? ConfidentialResourceAccessSelfGrantCommand
               : ConfidentialResourceAccessRequestCreateCommand;
         const command = new Command();
         command.resourceId = props.resourceId;
         command.resourceType = props.resourceType;
         return command;
      },
      {
         onSuccess: () => {
            const currentConfidentialAccessLevel = getCurrentPractitioner()?.confidentialAccessLevel;
            const snackbarMessage =
               currentConfidentialAccessLevel === 'MONITORED'
                  ? translationKeys.confidentialAccessSelfGrantSuccess
                  : translationKeys.confidentialAccessRequestSuccess;

            dispatchSnackbarPositiveEvent(snackbarMessage);
            refetchCqrsQuery(props.refetchQuery);
         },
      }
   );

   if (!confidentialAccessLevel) {
      return null;
   }

   const handleClick: PromiseVoidFunction = async () => {
      await requestAccess();
   };

   return (
      <Button
         testId={'confidentialAccessRequest'}
         variant={ButtonVariant.SECONDARY}
         onClick={handleClick}
         compact
         nowrap
         label={
            <StyledFlex gap={'4px'} alignItems={'center'}>
               <IconWrapper size={IconSize.SMALL} fillColor={IconFillColor.PRIMARY} testId={'confidentialAccessIcon'}>
                  <ShieldProtectedIcon />
               </IconWrapper>
               <Translate>{translationKeys.confidentialAccessRequest}</Translate>
            </StyledFlex>
         }
      />
   );
};
